import _get from "lodash/get";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { useParams } from "react-router-dom";

import FormFieldsCard from "components/lib/Global/FormFieldsCard";
import {
  CORPORATES,
  INDIVIDUALS,
} from "components/PLCTABS/SFLdata/config/constants";
import { getFullName, getTransformedFormFields } from "utils";
import options from "components/PLCTABS/SFLdata/selectOptions";
import { Constants } from "config/constants";

// Helper function to compute changed fields and previous values
const getChangedFields = (
  official,
  latestVersion,
  viewRestrictedData,
  isCurrentUserRow
) => {
  const changedFields = [];

  // Find the official in latestVersion
  const latestOfficial = latestVersion?.find((o) => o._id === official._id);

  // If no latestOfficial, return empty (no changes to display)
  if (!latestOfficial) return changedFields;

  // Use official as the previous state (from previousVersion in CompanyOfficialSubRow)
  const previousOfficial = official;

  // Helper function to add field to changedFields if values differ
  const addToChangedFields = (
    name,
    key,
    currentValue,
    previousValue,
    isRestricted = false
  ) => {
    if (isRestricted && !(viewRestrictedData || isCurrentUserRow)) return;
    if (
      previousValue &&
      currentValue !== previousValue &&
      currentValue !== "N/A"
    ) {
      changedFields.push({
        name,
        key,
        value: currentValue,
      });
    }
  };

  // Compare names
  const currentFullName = getFullName(latestOfficial.names) || "N/A";
  const previousFullName = getFullName(previousOfficial.names) || "N/A";
  addToChangedFields(
    "fullName",
    "Full Name",
    currentFullName,
    previousFullName
  );

  const currentFormerName = _get(latestOfficial, "names.formerName", "N/A");
  const previousFormerName = _get(previousOfficial, "names.formerName", "N/A");
  addToChangedFields(
    "formerName",
    "Former name",
    currentFormerName,
    previousFormerName
  );

  // Compare electronicAddress (restricted fields)
  if (isCurrentUserRow || viewRestrictedData) {
    const currentEmail = _get(
      latestOfficial,
      "electronicAddress.emailAddress",
      "N/A"
    );
    const previousEmail = _get(
      previousOfficial,
      "electronicAddress.emailAddress",
      "N/A"
    );
    addToChangedFields(
      "emailAddress",
      "Email Address",
      currentEmail,
      previousEmail
    );

    const currentMobile = _get(
      latestOfficial,
      "electronicAddress.mobileNumber",
      "N/A"
    );
    const previousMobile = _get(
      previousOfficial,
      "electronicAddress.mobileNumber",
      "N/A"
    );
    addToChangedFields(
      "mobileNumber",
      "Mobile Number",
      currentMobile,
      previousMobile
    );
  }

  // Compare identificationDetails
  if (
    latestOfficial.identificationDetails &&
    previousOfficial.identificationDetails
  ) {
    const identificationFields = getTransformedFormFields({
      idType: official.idType,
      formType: "identificationDetails",
      formValues: latestOfficial.identificationDetails,
      formatDOB: true,
    });

    identificationFields.forEach((field) => {
      const currentValue = field.value;
      const previousValue = _get(
        previousOfficial.identificationDetails,
        field.value
      );
      const formattedPreviousValue =
        field.name === "dateOfBirth" && previousValue && previousValue !== "N/A"
          ? format(new Date(previousValue), "yyyy/MM/dd")
          : previousValue;
      addToChangedFields(
        field.name,
        field.key,
        currentValue,
        formattedPreviousValue,
        field.name === "companyKRAPIN" || field.name === "entityType"
      );
    });
  }

  // Compare corporate-specific fields
  if (CORPORATES.includes(official.idType)) {
    const currentCompanyName = _get(latestOfficial.names, "companyName", "N/A");
    const previousCompanyName = _get(
      previousOfficial.names,
      "companyName",
      "N/A"
    );
    addToChangedFields(
      "companyName",
      "Company Name",
      currentCompanyName,
      previousCompanyName
    );

    const currentFormerName = _get(
      latestOfficial.identificationDetails,
      "companyFormerName",
      "N/A"
    );
    const previousFormerName = _get(
      previousOfficial.identificationDetails,
      "companyFormerName",
      "N/A"
    );
    addToChangedFields(
      "formerName",
      "Company Former name",
      currentFormerName,
      previousFormerName
    );

    const currentRegDate = _get(
      latestOfficial.names,
      "dateOfRegistration",
      "N/A"
    );
    const previousRegDate = _get(
      previousOfficial.names,
      "dateOfRegistration",
      "N/A"
    );
    addToChangedFields(
      "dateOfRegistration",
      "Company Registration Date",
      currentRegDate,
      previousRegDate
    );

    const currentRegNumber = _get(latestOfficial, "companyRegNumber", "N/A");
    const previousRegNumber = _get(previousOfficial, "companyRegNumber", "N/A");
    addToChangedFields(
      "companyRegNumber",
      "Company Registration Number",
      currentRegNumber,
      previousRegNumber
    );

    const currentCountry = _get(
      latestOfficial.identificationDetails,
      "countryOfIncorporation",
      "N/A"
    );
    const previousCountry = _get(
      previousOfficial.identificationDetails,
      "countryOfIncorporation",
      "N/A"
    );
    addToChangedFields(
      "countryOfIncorporation",
      "Country of Incorporation",
      currentCountry,
      previousCountry
    );

    if (viewRestrictedData) {
      const currentKRAPIN = _get(
        latestOfficial.identificationDetails,
        "companyKRAPIN",
        "N/A"
      );
      const previousKRAPIN = _get(
        previousOfficial.identificationDetails,
        "companyKRAPIN",
        "N/A"
      );
      addToChangedFields(
        "companyKRAPIN",
        "Company's KRA PIN",
        currentKRAPIN,
        previousKRAPIN,
        true
      );

      const currentEntityType = _get(
        latestOfficial.identificationDetails,
        "entityType",
        "N/A"
      );
      const previousEntityType = _get(
        previousOfficial.identificationDetails,
        "entityType",
        "N/A"
      );
      addToChangedFields(
        "entityType",
        "Entity Type",
        currentEntityType,
        previousEntityType,
        true
      );
    }
  }

  // Compare date fields
  const currentAppointmentDate = latestOfficial?.appointmentDate
    ? format(new Date(latestOfficial.appointmentDate), "yyyy/MM/dd")
    : "N/A";
  const previousAppointmentDate = previousOfficial?.appointmentDate
    ? format(new Date(previousOfficial.appointmentDate), "yyyy/MM/dd")
    : "N/A";
  addToChangedFields(
    `effectiveDateOfBecoming${official.designation}`,
    `Effective Date Of Becoming ${official.designation}`,
    currentAppointmentDate,
    previousAppointmentDate
  );

  const currentCessationDate = latestOfficial?.cessationDate
    ? format(new Date(latestOfficial.cessationDate), "yyyy/MM/dd")
    : "N/A";
  const previousCessationDate = previousOfficial?.cessationDate
    ? format(new Date(previousOfficial.cessationDate), "yyyy/MM/dd")
    : "N/A";
  addToChangedFields(
    `dateOfCeasingToBeA${official.designation}`,
    `Date of Ceasing to be a ${official.designation}`,
    currentCessationDate,
    previousCessationDate
  );

  return changedFields;
};

function GeneralInformation({
  official,
  isCompanyDashboard,
  isHistory,
  isChangeList,
  isInTransactionEditContext,
  isCurrentUserRow,
  viewRestrictedData,
  hideResolveAllCheckbox,
  data,
  latestVersion,
}) {
  const isJointShareholder =
    official?.designation === Constants.APPLICANT_CAPACITIES.JOINT_SHAREHOLDER;
  let fullName = getFullName(official.names);
  const { trxId } = useParams();
  const { initiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );

  // Get current transaction data
  const currentTransaction = initiatedTransactions?.items?.find(
    (transaction) => transaction._id === trxId
  );

  // Compute changed fields and previous values when isHistory is true
  const changedFields = isHistory
    ? getChangedFields(
        official,
        latestVersion,
        viewRestrictedData,
        isCurrentUserRow
      )
    : [];

  if (isChangeList) {
    // When isChangeList is true, only show the cessation date
    const cessationDate =
      isInTransactionEditContext && currentTransaction?.cessationDate
        ? format(new Date(currentTransaction.cessationDate), "yyyy/MM/dd")
        : official?.cessationDate
        ? format(new Date(official.cessationDate), "yyyy/MM/dd")
        : data
            ?.map((item) =>
              item.cessationDate
                ? format(new Date(item.cessationDate), "yyyy/MM/dd")
                : null
            )
            .filter(Boolean)
            .join(", ") || "N/A";

    return (
      <FormFieldsCard
        fields={[
          {
            key: `Date of Ceasing to be a ${official.designation}`,
            name: `dateOfCeasingToBeA${official.designation}`,
            value: cessationDate,
          },
        ]}
        formName="beneficialOwnershipForm"
        formHelperKey="beneficialOwnershipForm"
        hideResolveAllCheckbox={hideResolveAllCheckbox}
      />
    );
  }

  // Original implementation for when isChangeList is false
  const namesFormFields = [];
  let identificationFormFields = [];

  const getJSstringName = () => {
    const JSnames =
      official?.jointShareholders?.map(({ names }) => names) || [];
    const fullNames = JSnames.map((name) => getFullName(name)); // clean full names

    if (fullNames.length === 0) return "";
    if (fullNames.length === 1) return fullNames[0];

    if (fullNames.length === 2) {
      return `${fullNames[0]} & ${fullNames[1]}`;
    }

    // More than 2 names → Oxford list with "&"
    const last = fullNames.pop();
    return `${fullNames.join(", ")} & ${last}`;
  };

  if (isJointShareholder && official?.jointShareholders?.length > 0) {
    fullName = getJSstringName();
  }

  if (INDIVIDUALS.includes(official.idType)) {
    namesFormFields.push(
      {
        key: "Full Name",
        name: "fullName",
        value: fullName || "N/A",
      },
      {
        key: "Former name",
        name: "formerName",
        value:
          _get(official.names, "formerName", "N/A") ||
          getFullName(official.names) ||
          "N/A",
      }
    );
    // Include electronicAddress fields if isCurrentUserRow or viewRestrictedData
    if ((isCurrentUserRow || viewRestrictedData) && !isJointShareholder) {
      namesFormFields.push(
        {
          key: "Email Address",
          name: "emailAddress",
          value: _get(official, "electronicAddress.emailAddress", "N/A"),
        },
        {
          key: "Mobile Number",
          name: "mobileNumber",
          value: _get(official, "electronicAddress.mobileNumber", "N/A"),
        }
      );
    }

    identificationFormFields = getTransformedFormFields({
      idType: official.idType,
      formType: "identificationDetails",
      formValues: official.identificationDetails,
      formatDOB: Boolean(isCurrentUserRow || viewRestrictedData),
    }).map((field) => ({
      ...field,
      value: field.value,
    }));
  }

  if (CORPORATES.includes(official.idType)) {
    const countryValue = _get(
      official.identificationDetails,
      "countryOfIncorporation",
      "N/A"
    );
    const country = options.countries.find(
      (country) => country.value === countryValue
    )?.label;
    namesFormFields.push(
      {
        key: "Company Name",
        name: "companyName",
        value: _get(official.names, "companyName", "N/A"),
      },
      {
        key: "Company Former name",
        name: "formerName",
        value:
          _get(official.identificationDetails, "companyFormerName") ||
          getFullName(official.names) ||
          "N/A",
      },
      {
        key: "Company Registration Date",
        name: "dateOfRegistration",
        value: official.names.dateOfRegistration
          ? official.names.dateOfRegistration
          : "N/A",
      },
      {
        key: "Company Registration Number",
        name: "companyRegNumber",
        value: _get(official, "companyRegNumber", "N/A"),
      },
      {
        name: "countryOfIncorporation",
        key: "Country of Incorporation",
        value: country,
      }
    );
    if (viewRestrictedData) {
      namesFormFields.push(
        {
          name: "companyKRAPIN",
          key: "Company's KRA PIN",
          value: _get(official.identificationDetails, "companyKRAPIN", "N/A"),
        },
        {
          key: "Entity Type",
          name: "entityType",
          value: _get(official.identificationDetails, "entityType", "N/A"),
        }
      );
    }
  }

  const datesFormFields = [
    {
      key: `Effective Date Of Becoming ${official.designation}`,
      name: `effectiveDateOfBecoming${official.designation}`,
      value: official?.appointmentDate
        ? format(new Date(official.appointmentDate), "yyyy/MM/dd")
        : "N/A",
    },
    {
      key: `Date of Ceasing to be a ${official.designation}`,
      name: `dateOfCeasingToBeA${official.designation}`,
      value:
        isInTransactionEditContext && currentTransaction?.cessationDate
          ? format(new Date(currentTransaction.cessationDate), "yyyy/MM/dd")
          : official?.cessationDate
          ? format(new Date(official.cessationDate), "yyyy/MM/dd")
          : data
              ?.map((item) =>
                item.cessationDate
                  ? format(new Date(item.cessationDate), "yyyy/MM/dd")
                  : null
              )
              .filter(Boolean)
              .join(", ") || "N/A",
    },
  ];

  let namesIdentificationFields = [...namesFormFields];
  if (!isJointShareholder) {
    namesIdentificationFields = [
      ...namesIdentificationFields,
      ...identificationFormFields,
    ];
  }

  return (
    <>
      {isCompanyDashboard ? (
        <FormFieldsCard
          fields={namesIdentificationFields}
          hideResolveAllCheckbox={isCompanyDashboard}
          changedFields={changedFields}
          isHistory={isHistory}
        />
      ) : (
        <>
          <FormFieldsCard
            fields={namesFormFields}
            hideResolveAllCheckbox={hideResolveAllCheckbox}
            changedFields={changedFields}
            isHistory={isHistory}
          />
          {!isJointShareholder && (
            <FormFieldsCard
              fields={identificationFormFields}
              hideResolveAllCheckbox={hideResolveAllCheckbox}
              changedFields={changedFields}
              isHistory={isHistory}
            />
          )}
        </>
      )}
      <FormFieldsCard
        fields={datesFormFields}
        formName="beneficialOwnershipForm"
        formHelperKey="beneficialOwnershipForm"
        hideResolveAllCheckbox={hideResolveAllCheckbox}
        changedFields={changedFields.filter(
          (field) =>
            field.name.startsWith("effectiveDateOfBecoming") ||
            field.name.startsWith("dateOfCeasingToBeA")
        )}
        isHistory={isHistory}
      />
    </>
  );
}

export default GeneralInformation;
